import { useClerk, useUser } from "@clerk/clerk-react";
import type { BillingInterval, SelfServePlanKey } from "@giftforward/entitlements";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMorphCard } from "@/hooks/useMorphCard";
import { type OnboardingStep, useOnboardingState } from "@/hooks/useOnboardingState";
import { POST_ONBOARDING_REDIRECT_URL, useOnboardingSubmit } from "@/hooks/useOnboardingSubmit";
import { PAGE_VIEW_STATE, toAnalyticsPlan, updatePendingSignUpPlan } from "@/lib/analytics";
import { userFacingErrorMessage } from "@/utils/userFacingError-helper";
import { OnboardingPaymentDrawer } from "./OnboardingPaymentDrawer";
import { OnboardingPaymentStep } from "./OnboardingPaymentStep";
import { OnboardingPlanStep } from "./OnboardingPlanStep";
import { OnboardingProfileStep } from "./OnboardingProfileStep";
import { OnboardingProgress } from "./OnboardingProgress";
import { OnboardingSetupStep } from "./OnboardingSetupStep";
import { OnboardingTeamStep } from "./OnboardingTeamStep";

/**
 * OnboardingWizard — controller for the multi-step post-signup flow.
 *
 * Flow: profile → plan → setup. Starter finishes there (1 user, no invite).
 * Pro continues to payment, and only then to the team/invite step (max 2
 * teammates) — the seats being filled are the ones the charge just bought.
 *
 * The organization is created at SETUP, on its own screen, for both plans.
 * Every later step assumes it exists — which is what lets the payment drawer
 * open straight onto a live card form instead of a placeholder.
 *
 * Invited users (who already have a groupId) are detected on mount and
 * redirected straight to the dashboard — they never see this wizard.
 */
export function OnboardingWizard() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const checkedInvitedFor = useRef<string | null>(null);

  const {
    step,
    formData,
    isSubmitting,
    error,
    hasOrganization,
    markOrganizationCreated,
    updateFormData,
    setStep,
    setSubmitting,
    setError,
    clearPersistedState,
    validateProfile,
  } = useOnboardingState();

  // The numbered steps, fixed at three.
  //
  // This used to be derived from the selected plan — two steps for Starter, three for Pro — which
  // meant the plan step read "Step 2 of 2" to a client who had not chosen yet, and then jumped to
  // "of 3" the moment they picked Pro. A total that changes underneath the client reads as a bug.
  // Starter simply finishes before the third: ending early is ordinary, a moving total is not.
  const wizardFlow: OnboardingStep[] = ["profile", "plan", "team"];

  // Payment drawer state
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  /** Set when the organization could not be created. Rendered by the setup step that ran it. */
  const [setupError, setSetupError] = useState<string | null>(null);
  /** Guards against a second plan click starting a second organization create. */
  const setupInFlight = useRef(false);

  // Card morph animation hook
  const cardRef = useMorphCard(step);

  const { handleFinish, ensureOrganization } = useOnboardingSubmit({
    formData,
    setSubmitting,
    setError,
    clearPersistedState,
    hasOrganization,
    markOrganizationCreated,
  });

  // ── Invited-user guard ──────────────────────────────────────────────
  // Reload the Clerk user on mount to pick up publicMetadata.groupId
  // written by the user.created webhook. If the user already belongs to
  // an org (invited flow), skip onboarding and go straight to the app.
  //
  // `hasOrganization` is what keeps this from firing on our OWN clients. A groupId used to mean
  // exactly one thing here — an invited user in the wrong place — because the organization was not
  // created until the wizard's last act. The setup step creates it in the middle, so from setup
  // onwards a Pro client legitimately has a groupId while still owing us a payment, and every
  // remount of this component would otherwise read that as "invited" and eject them to the
  // dashboard: no card collected, no invitations sent, sitting on Starter having chosen Pro.
  // The ref below cannot carry that knowledge across a remount, which is why the flag is persisted.
  useEffect(() => {
    if (hasOrganization) return;
    // React StrictMode runs useEffect twice. This ref ensures we only call reload once.
    if (!user || checkedInvitedFor.current === user.id) return;
    checkedInvitedFor.current = user.id;

    user
      .reload()
      .then(() => {
        const metadata = user.publicMetadata as Record<string, unknown>;
        if (typeof metadata?.groupId === "number" && metadata.groupId > 0) {
          navigate(POST_ONBOARDING_REDIRECT_URL, { replace: true, state: PAGE_VIEW_STATE });
        }
      })
      .catch((err) => {
        console.warn("[Onboarding] Failed to reload user for invitation check:", err);
        // Reload failed — fall through to the form; the backend will catch it on submit.
      });
  }, [user, navigate, hasOrganization]);

  // "setup" and "payment" are not numbered steps — the bar holds at the plan step's number through
  // both, rather than blanking out or inventing extra dots.
  const progressStep: OnboardingStep = step === "setup" || step === "payment" ? "plan" : step;
  const currentStepIndex = wizardFlow.indexOf(progressStep) + 1;
  const totalSteps = wizardFlow.length;

  // ── Step handlers ───────────────────────────────────────────────────

  // 1. Profile Handlers
  const handleProfileContinue = useCallback(() => {
    const err = validateProfile();
    if (err) {
      setError(err);
      return;
    }
    setStep("plan");
  }, [validateProfile, setError, setStep]);

  // 2. Plan Handlers

  /**
   * Picking a plan runs the account setup, on the setup step's own screen.
   *
   * The plan is passed in rather than read off `formData`: `updateFormData` is a state update, so
   * the value is not readable in the same tick that sets it.
   *
   * Setup is the wizard's point of no return — Back exists only from the plan step
   * ({@link handleBack}), so this is the last moment the organization name can still change. That
   * is what makes it the right place to create the organization, and why it is not done earlier
   * where an edit would leave the organization named something the client has since replaced.
   */
  const runSetup = useCallback(
    async (plan: "free" | SelfServePlanKey) => {
      // Back from payment lands here a second time. The organization already exists then, so
      // nothing from the first pass runs again — no setup screen, no onboard call, no session
      // reload. Only the plan choice moves on.
      if (hasOrganization) {
        if (plan !== "free") {
          setStep("payment");
          setIsPaymentDrawerOpen(true);
          return;
        }
        handleFinish();
        return;
      }
      if (setupInFlight.current) return;
      setupInFlight.current = true;
      setSetupError(null);
      setStep("setup");
      try {
        await ensureOrganization();
      } catch (err) {
        // The step's title already says "We couldn't finish setting up", so the line shows only
        // the reason, mapped to a sentence that is safe to show (GFG-427).
        setSetupError(userFacingErrorMessage(err));
        return;
      } finally {
        setupInFlight.current = false;
      }
      // A paid plan collects a card next, which needs the organization that now exists. Starter
      // has nothing left to ask for.
      if (plan !== "free") {
        setStep("payment");
        setIsPaymentDrawerOpen(true);
        return;
      }
      handleFinish();
    },
    [hasOrganization, ensureOrganization, handleFinish, setStep],
  );

  const handlePlanPickStarter = useCallback(() => {
    updateFormData({ selectedPlan: "free" });
    void runSetup("free");
  }, [updateFormData, runSetup]);

  const handlePlanPickPaid = useCallback(
    (plan: SelfServePlanKey) => {
      // Annual is the default every time a paid plan is picked; the payment drawer offers Monthly.
      updateFormData({ selectedPlan: plan, selectedInterval: "year" });
      void runSetup(plan);
    },
    [updateFormData, runSetup],
  );

  const handleSetupRetry = useCallback(() => {
    void runSetup(formData.selectedPlan);
  }, [runSetup, formData.selectedPlan]);

  // 3. Payment Handlers

  /**
   * The subscription is paid for. Only now is the client on Pro, and only now do the extra seats
   * the team step offers to fill actually exist.
   */
  const handlePaymentSuccess = useCallback(() => {
    updatePendingSignUpPlan(toAnalyticsPlan(formData.selectedPlan));
    setIsPaymentDrawerOpen(false);
    setStep("team");
  }, [setStep, formData.selectedPlan]);

  // Closing the drawer returns to the payment step, which offers the drawer again or Back to the
  // plan cards.
  const handlePaymentClose = useCallback(() => {
    setIsPaymentDrawerOpen(false);
  }, []);

  const handleOpenPaymentDrawer = useCallback(() => {
    setIsPaymentDrawerOpen(true);
  }, []);

  const handleIntervalChange = useCallback(
    (interval: BillingInterval) => updateFormData({ selectedInterval: interval }),
    [updateFormData],
  );

  // 4. Team Handlers
  // Pro-only, and reached only through a completed charge, so both of these are the wizard's last
  // act: upload the avatar, send the invitations, go to the dashboard.
  const handleTeamContinue = useCallback(() => {
    handleFinish();
  }, [handleFinish]);

  const handleTeamSkip = useCallback(() => {
    handleFinish({ skipInvites: true });
  }, [handleFinish]);

  const handleEmailChange = useCallback(
    (index: number, value: string) => {
      const emails: string[] = [...formData.invitedEmails];
      emails[index] = value;
      updateFormData({ invitedEmails: emails });
    },
    [formData.invitedEmails, updateFormData],
  );

  // 5. Shared Handlers
  const handleBack = useCallback(() => {
    // Payment → plan: nothing has been charged, so the client can compare the plans again.
    if (step === "payment") {
      setIsPaymentDrawerOpen(false);
      setStep("plan");
      return;
    }
    // Plan → profile only before setup. After it the organization exists under the name given
    // there, and an edit on the profile step would change nothing.
    if (step === "plan" && !hasOrganization) {
      setStep("profile");
    }
    // No back from "team" — payment is already processed.
    // No back from "profile" — it's the first step.
  }, [step, setStep, hasOrganization]);

  /**
   * The only way out of the wizard, and the reason it exists: the session is live from the moment
   * the verification code is accepted, so /sign-in and /sign-up both bounce a half-onboarded client
   * straight back here, and the app's own sign-out sits behind RequireOnboarded. Without this the
   * only exits are clearing cookies or a private window.
   *
   * The persisted state goes first — whoever signs in next on this tab should not inherit the
   * previous person's half-typed organization.
   */
  const handleSignOut = useCallback(() => {
    clearPersistedState();
    void signOut({ redirectUrl: "/sign-in" });
  }, [clearPersistedState, signOut]);

  // Read a file to a data URL for immediate preview
  const handleAvatarFileSelected = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (typeof e.target?.result === "string") {
          updateFormData({
            avatarFile: file,
            avatarPreviewUrl: e.target.result,
          });
        }
      };
      reader.onerror = () => {
        setError("Failed to read image file. Please try another one.");
      };
      reader.readAsDataURL(file);
    },
    [updateFormData, setError],
  );

  // Show a brief loader while we check if the user is invited
  if (!user) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <CircularProgress
          aria-label="Loading onboarding"
          sx={{ color: "var(--color-dark-dust)" }}
        />
      </div>
    );
  }

  // Back: plan → profile until the organization exists; payment → plan always. Hidden on Team.
  const showBack = (step === "plan" && !hasOrganization) || step === "payment";

  // Named beside the sign-out, because "Not you?" is unanswerable without it: nothing else on this
  // card says which account the wizard is filling in. Falls back to the first address on file — a
  // client who signed up through Google may have no primary set.
  const signedInAs =
    user.primaryEmailAddress?.emailAddress ?? user.emailAddresses[0]?.emailAddress ?? null;

  return (
    <>
      <div
        ref={cardRef}
        className={`gfg-auth-card gfg-ob-card ${step === "plan" ? "is-wide" : ""}`}
      >
        {showBack && (
          <button type="button" className="gfg-auth-back" onClick={handleBack}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Back
          </button>
        )}

        <OnboardingProgress currentStep={currentStepIndex} totalSteps={totalSteps} />

        {step === "profile" && (
          <OnboardingProfileStep
            firstName={formData.firstName}
            lastName={formData.lastName}
            orgName={formData.orgName}
            avatarPreviewUrl={formData.avatarPreviewUrl}
            error={error}
            isSubmitting={isSubmitting}
            termsAccepted={formData.termsAccepted}
            // Both values move together: the tick is only meaningful alongside the version that was
            // on screen when it happened, and unticking clears the version so a later publish
            // cannot leave a stale one attached to a fresh tick.
            onTermsAcceptedChange={(accepted, version) =>
              updateFormData({ termsAccepted: accepted, termsVersion: accepted ? version : null })
            }
            onFirstNameChange={(val) => updateFormData({ firstName: val })}
            onLastNameChange={(val) => updateFormData({ lastName: val })}
            onOrgNameChange={(val) => updateFormData({ orgName: val })}
            onAvatarFileSelected={handleAvatarFileSelected}
            onContinue={handleProfileContinue}
          />
        )}

        {step === "plan" && (
          <OnboardingPlanStep
            error={error}
            isSubmitting={isSubmitting}
            onPickStarter={handlePlanPickStarter}
            onPickPlan={handlePlanPickPaid}
          />
        )}

        {step === "setup" && (
          <OnboardingSetupStep
            firstName={formData.firstName}
            lastName={formData.lastName}
            orgName={formData.orgName}
            // `error` as well as `setupError`: the Starter path runs handleFinish from here, and it
            // reports failure by setting `error` and stopping. Without this the client watches a
            // spinner that nothing will ever resolve.
            error={setupError ?? error}
            onRetry={handleSetupRetry}
          />
        )}

        {step === "payment" && formData.selectedPlan !== "free" && (
          <OnboardingPaymentStep
            plan={formData.selectedPlan}
            interval={formData.selectedInterval}
            onOpenDrawer={handleOpenPaymentDrawer}
          />
        )}

        {step === "team" && formData.selectedPlan !== "free" && (
          <OnboardingTeamStep
            plan={formData.selectedPlan}
            interval={formData.selectedInterval}
            invitedEmails={formData.invitedEmails}
            onEmailChange={handleEmailChange}
            onContinue={handleTeamContinue}
            onSkip={handleTeamSkip}
            isSubmitting={isSubmitting}
            error={error}
            subscriptionConfirmed
          />
        )}

        {/* Offered only while the organization does not exist. After setup the client is no longer
            trapped — RequireOnboarded lets them into the app — and a sign-out mid-payment would be
            an invitation to abandon a flow they have already half-paid for. */}
        {!hasOrganization && (
          <div className="gfg-ob-signout">
            {signedInAs && <span className="gfg-ob-signout-who">Signed in as {signedInAs}</span>}
            <span className="gfg-ob-signout-action">
              Not you?{" "}
              <button type="button" onClick={handleSignOut}>
                Sign out
              </button>
            </span>
          </div>
        )}
      </div>

      <OnboardingPaymentDrawer
        plan={formData.selectedPlan === "free" ? null : formData.selectedPlan}
        interval={formData.selectedInterval}
        onIntervalChange={handleIntervalChange}
        isOpen={isPaymentDrawerOpen}
        onClose={handlePaymentClose}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </>
  );
}
